import { useQuery } from "@tanstack/react-query";
import { Sparkles, Target, Users, Calendar } from "lucide-react";
import clsx from "clsx";

import { starApi } from "@/lib/extensions-api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Skeleton } from "@/components/ui/Skeleton";
import { Badge } from "@/components/ui/Badge";
import type { StarOffer } from "@/types/star";

const PERIOD_ICONS = {
  daily:   { label: "Daily",   color: "text-brand-600 bg-brand-50" },
  weekly:  { label: "Weekly",  color: "text-success-600 bg-success-50" },
  monthly: { label: "Monthly", color: "text-warning-600 bg-warning-50" },
  yearly:  { label: "Yearly",  color: "text-danger-600 bg-danger-50" },
};

export function StarOfferCard({ offer }: { offer: StarOffer }) {
  const progress = useQuery({
    queryKey: ["star-offer-progress", offer.id],
    queryFn: () => starApi.offerProgress(offer.id),
    refetchInterval: 30_000,
  });

  const period = PERIOD_ICONS[offer.period_type];
  const ownPct = progress.data
    ? Math.min(100, (progress.data.own / offer.own_point_target) * 100)
    : 0;
  const combinedPct = progress.data
    ? Math.min(100, (progress.data.combined / offer.combined_point_target) * 100)
    : 0;
  const qualified = progress.data?.qualified ?? false;

  return (
    <div className={clsx(
      "card-elevated overflow-hidden transition-all",
      qualified && "ring-2 ring-success-400 shadow-elevate-2",
    )}>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className={clsx("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-2xs font-bold uppercase", period.color)}>
              <Calendar className="h-3 w-3" /> {period.label}
            </div>
            <h3 className="mt-2 text-lg font-bold text-surface-900">{offer.name}</h3>
            {offer.description && (
              <p className="mt-1 text-xs text-surface-500 line-clamp-2">{offer.description}</p>
            )}
          </div>
          {qualified && (
            <Badge tone={offer.reward_strategy === "pool" ? "warning" : "success"}>
              {offer.reward_strategy === "pool" ? "✓ Qualified (Awaiting Close)" : "✓ Paid"}
            </Badge>
          )}
        </div>

        {/* Replace the Bonus Box with this: */}
        <div className="mt-4 rounded-lg bg-gradient-to-br from-warning-50 to-warning-100 p-3 flex items-center gap-2 border border-warning-200">
          <Sparkles className="h-5 w-5 text-warning-600" />
          <div>
            <div className="text-2xs font-semibold uppercase text-warning-700">
              {offer.reward_strategy === "pool" ? "Current Pool Fund" : "Bonus"}
            </div>
            <div className="text-xl font-bold text-warning-900">
              {offer.reward_strategy === "pool" 
                ? formatCurrency(progress.data?.current_pool_fund ?? 0) 
                : formatCurrency(offer.bonus_amount)}
            </div>
            {offer.reward_strategy === "pool" && (
              <div className="text-2xs text-warning-800 opacity-80">
                Shared equally among all qualifiers at period end
              </div>
            )}
          </div>
        </div>

        {progress.isLoading ? (
          <div className="mt-4 space-y-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : progress.data && (
          <div className="mt-4 space-y-3">
            <ProgressRow
              icon={<Target className="h-4 w-4" />}
              label="Own Points"
              current={progress.data.own}
              target={offer.own_point_target}
              pct={ownPct}
              tone="brand"
            />
            <ProgressRow
              icon={<Users className="h-4 w-4" />}
              label="Combined Points"
              current={progress.data.combined}
              target={offer.combined_point_target}
              pct={combinedPct}
              tone="success"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressRow({
  icon, label, current, target, pct, tone,
}: {
  icon: React.ReactNode;
  label: string;
  current: number;
  target: number;
  pct: number;
  tone: "brand" | "success";
}) {
  const barColor = tone === "brand" ? "bg-brand-500" : "bg-success-500";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-surface-700">
          {icon} {label}
        </div>
        <div className="text-xs font-mono text-surface-600">
          {formatNumber(current)} / {formatNumber(target)}
        </div>
      </div>
      <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
        <div className={clsx("h-full transition-all duration-500", barColor)}
              style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}